import {useEffect, useMemo, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {apiUrl} from '../../../../api-utils/constantVariables'

const passwordRules = Yup.string()
  .min(12, 'At least 12 characters')
  .matches(/[A-Z]/, 'Need uppercase letter')
  .matches(/[a-z]/, 'Need lowercase letter')
  .matches(/[0-9]/, 'Need a number')
  .matches(/[^A-Za-z0-9]/, 'Need a special character')
  .required('Required')

const schema = Yup.object().shape({
  new_password: passwordRules,
  confirm: Yup.string()
    .oneOf([Yup.ref('new_password')], 'Passwords must match')
    .required('Required'),
})

export function AcceptInvite() {
  const [params] = useSearchParams()
  const token = useMemo(() => params.get('token') || '', [params])
  const [preview, setPreview] = useState<{
    email: string
    name: string | null
    role: {name: string}
  } | null>(null)
  const [previewErr, setPreviewErr] = useState('')
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    if (!token) return
    void axios
      .get(`${apiUrl}/admin/auth/invite/${encodeURIComponent(token)}`)
      .then((res) => {
        setPreview(res.data?.data || null)
        setPreviewErr('')
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || 'Invalid or expired invite.'
        setPreviewErr(typeof msg === 'string' ? msg : 'Invalid or expired invite.')
      })
  }, [token])

  const formik = useFormik({
    initialValues: {new_password: '', confirm: ''},
    validationSchema: schema,
    onSubmit: async (values) => {
      setError('')
      try {
        const res = await axios.post(`${apiUrl}/admin/auth/invite/accept`, {
          token,
          password: values.new_password,
        })
        const payload = res.data?.data
        if (payload?.token && payload?.admin) {
          const admin = {
            ...(payload.admin || {}),
            JWToken: payload.token,
            must_change_password: !!payload.admin?.must_change_password,
          }
          localStorage.setItem('admin', JSON.stringify(admin))
          window.dispatchEvent(new Event('shrido-admin-profile-changed'))
          navigate('/dashboard?welcome=1')
          return
        }
        setError('Unexpected response from server.')
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.response?.data?.error || 'Could not accept invite.'
        setError(typeof msg === 'string' ? msg : 'Could not accept invite.')
      }
    },
  })

  if (!token) {
    return (
      <div className='text-center'>
        <p>Invalid or missing invite link.</p>
        <Link to='/auth/login'>Sign in</Link>
      </div>
    )
  }

  if (previewErr) {
    return (
      <div className='text-center'>
        <div className='alert alert-danger'>{previewErr}</div>
        <Link to='/auth/login'>Sign in</Link>
      </div>
    )
  }

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>Accept invitation</h1>
        {preview ? (
          <div className='text-gray-600 fw-semibold fs-6'>
            <div>
              {preview.name ? `${preview.name} · ` : null}
              {preview.email}
            </div>
            <div className='mt-2'>Role: {preview.role?.name}</div>
          </div>
        ) : (
          <div className='text-gray-500'>Checking invite…</div>
        )}
      </div>
      {error ? <div className='alert alert-danger mb-5'>{error}</div> : null}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder'>New password</label>
        <input
          type='password'
          autoComplete='new-password'
          {...formik.getFieldProps('new_password')}
          className={clsx(
            'form-control bg-transparent',
            formik.touched.new_password && formik.errors.new_password && 'is-invalid'
          )}
        />
        {formik.touched.new_password && formik.errors.new_password && (
          <div className='text-danger fs-7 mt-1'>{formik.errors.new_password}</div>
        )}
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder'>Confirm password</label>
        <input
          type='password'
          autoComplete='new-password'
          {...formik.getFieldProps('confirm')}
          className={clsx(
            'form-control bg-transparent',
            formik.touched.confirm && formik.errors.confirm && 'is-invalid'
          )}
        />
        {formik.touched.confirm && formik.errors.confirm && (
          <div className='text-danger fs-7 mt-1'>{formik.errors.confirm}</div>
        )}
      </div>

      <div className='d-grid mb-5'>
        <button
          type='submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !preview}
        >
          Activate account
        </button>
      </div>
      <div className='text-center'>
        <Link to='/auth/login'>Back to sign in</Link>
      </div>
    </form>
  )
}
